import { Controller } from "@hotwired/stimulus"
import { toast as toastService } from "../services/toast"

/**
 * Controlador para el workspace de órdenes de venta
 * Maneja el flujo de: escaneo → agregar producto → modificar cantidad → enviar
 * 
 * Uso:
 * <div data-controller="sale-order-workspace"
 *      data-sale-order-workspace-scan-url-value="/venta/ordenes/scan"
 *      data-sale-order-workspace-order-id-value="123">
 */
export default class extends Controller {
  static targets = [
    "currentOrderPanel",
    "pendingOrdersPanel", 
    "itemsContainer",
    "totalAmount",
    "itemsCount",
    "emptyState",
    "orderActions",
    "pendingBadge"
  ]
  
  static values = {
    scanUrl: String,
    addItemUrl: String,
    updateItemUrl: String,
    removeItemUrl: String,
    orderId: Number
  }

  connect() {
    // Escuchar eventos del scanner
    this.handleScanBound = this.handleScan.bind(this)
    document.addEventListener("scanner:detected", this.handleScanBound)
    
    // Escuchar actualizaciones via Turbo Stream
    this.element.addEventListener("turbo:submit-end", this.handleFormSubmit.bind(this))
    
    // Escuchar notificaciones de ActionCable para cambios de estado de órdenes
    this.subscribeToOrderUpdates()
  }

  disconnect() {
    document.removeEventListener("scanner:detected", this.handleScanBound)
    this.unsubscribeFromOrderUpdates()
  }

  // Suscribirse a actualizaciones de órdenes via ActionCable
  subscribeToOrderUpdates() {
    if (typeof App !== 'undefined' && App.cable) {
      this.orderSubscription = App.cable.subscriptions.create(
        { channel: "SaleOrderChannel", user_type: "seller", user_id: this.getCurrentUserId() },
        {
          received: (data) => {
            if (data.action === "status_changed") {
              this.handleOrderStatusChange(data)
            }
          }
        }
      )
    }
  }

  unsubscribeFromOrderUpdates() {
    if (this.orderSubscription) {
      this.orderSubscription.unsubscribe()
    }
  }

  getCurrentUserId() {
    // Obtener el user_id del meta tag o del body data attribute
    return document.querySelector('meta[name="current-user-id"]')?.content || 
           document.body.dataset.userId
  }

  handleOrderStatusChange(data) {
    const statusMessages = {
      processing: `La orden #${data.order_id} está siendo procesada por caja`,
      completed: `¡Venta #${data.order_id} completada! 🎉`,
      cancelled: `La orden #${data.order_id} fue cancelada`
    }
    
    const message = statusMessages[data.status]
    if (message) {
      const type = data.status === "completed" ? "success" : 
                   data.status === "cancelled" ? "warning" : "info"
      this.showToast(message, type)
    }
  }

  // Manejar código escaneado desde el scanner
  async handleScan(event) {
    const { code } = event.detail
    
    if (!code) return
    
    try {
      const response = await fetch(this.scanUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({ code })
      })
      
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else if (response.status === 404) {
        this.showToast(`Producto no encontrado: ${code}`, "error")
      } else {
        this.showToast("Error al agregar producto", "error")
      }
    } catch (error) {
      console.error("Error scanning:", error)
      this.showToast("Error de conexión", "error")
    }
  }

  // Actualizar cantidad de un item
  async updateQuantity(event) {
    const input = event.currentTarget
    const itemId = input.dataset.itemId
    const maxStock = parseInt(input.dataset.maxStock) || parseInt(input.max) || Infinity
    let quantity = parseInt(input.value) || 0
    
    // Validar límites
    if (quantity < 1) {
      quantity = 1
      input.value = 1
    }
    
    if (quantity > maxStock) {
      quantity = maxStock
      input.value = maxStock
      this.showToast(`Stock máximo disponible: ${maxStock}`, "warning")
    }
    
    // Debounce para evitar muchas peticiones
    clearTimeout(this.quantityTimeout)
    this.quantityTimeout = setTimeout(async () => {
      await this.sendQuantityUpdate(itemId, quantity)
    }, 300)
  }

  async sendQuantityUpdate(itemId, quantity) {
    try {
      const response = await fetch(`/venta/ordenes/${this.orderIdValue}/update_quantity`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({ item_id: itemId, quantity })
      })
      
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        this.showToast("Error al actualizar cantidad", "error")
      }
    } catch (error) {
      console.error("Error updating quantity:", error)
      this.showToast("Error de conexión", "error")
    }
  }

  // Incrementar cantidad
  incrementQuantity(event) {
    event.preventDefault()
    const button = event.currentTarget
    const input = button.parentElement.querySelector("input[type='number']")
    const currentValue = parseInt(input.value) || 0
    const maxStock = parseInt(button.dataset.maxStock) || parseInt(input.max) || Infinity
    
    if (currentValue < maxStock) {
      input.value = currentValue + 1
      input.dispatchEvent(new Event("change", { bubbles: true }))
    } else {
      this.showToast(`Stock máximo disponible: ${maxStock}`, "warning")
    }
  }

  // Decrementar cantidad
  decrementQuantity(event) {
    event.preventDefault()
    const button = event.currentTarget
    const input = button.parentElement.querySelector("input[type='number']")
    const currentValue = parseInt(input.value) || 0
    if (currentValue > 1) {
      input.value = currentValue - 1
      input.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  // Eliminar item
  async removeItem(event) {
    event.preventDefault()
    const button = event.currentTarget
    const itemId = button.dataset.itemId
    
    try {
      const response = await fetch(`/venta/ordenes/${this.orderIdValue}/remove_item`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({ item_id: itemId })
      })
      
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      console.error("Error removing item:", error)
      this.showToast("Error al eliminar", "error")
    }
  }

  // Manejar respuesta de formularios
  handleFormSubmit(event) {
    if (event.detail.success) {
      // El Turbo Stream ya actualiza la UI
    }
  }

  // Utilidades
  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  showToast(message, type = "info") {
    if (toastService && typeof toastService.show === "function") {
      toastService.show(message, type)
    }
  }
}
